<script lang="ts">
import type { StudyPayerMixNarrativeSlideProps } from '$lib/components/study/slide-layout-types'
import StudyBlueCalloutCard from '../cards/StudyBlueCalloutCard.svelte'
import StudyPayerSegmentBarListCard from '../cards/StudyPayerSegmentBarListCard.svelte'
import StudySlideDoughnutCard from '../cards/StudySlideDoughnutCard.svelte'
import StudySlideTextCard from '../cards/StudySlideTextCard.svelte'
import StudySlide from '../StudySlide.svelte'
import StudySlideContent from '../StudySlideContent.svelte'

/** Payer mix with segment bars + doughnut + cash narrative + footnote (`s6` GLP-style). */
let props: StudyPayerMixNarrativeSlideProps = $props()

const headlineStyle = props.headlineStyle ?? 'margin-bottom:.82rem;'
const introStyle = props.introStyle ?? 'margin-bottom:.95rem;'
const tcStyle = props.tcStyle ?? 'gap:2.1rem;align-items:start;'
const tcWeighted = $derived(props.tcWeighted ?? true)
const d = $derived(props.doughnut)
// When there's no chart/cash card, drop the two-column layout so the segment bar list
// expands full width. A lone footnote does NOT force a right column — it renders at the
// bottom (below) instead of floating as a card in an otherwise-empty right column.
const hasRightColumn = $derived(!!d || !!props.cashInsightCard)
</script>

<StudySlide
  id={props.id}
  active={props.active}
  extraBottom={props.extraBottom}
  background={props.background}
  slideClass={props.slideClass}
>
  <StudySlideContent
    maxWidth={props.maxWidth ?? '980px'}
    contentClass={props.contentClass}
    style={props.contentStyle}
  >
    <div class="ey">{@html props.eyebrow}</div>
    {#if hasRightColumn}
      <div class="tc" class:w={tcWeighted} style={tcStyle}>
        <div>
          <div class="hl" style={headlineStyle}>{@html props.headline}</div>
          <p class="bd" style={introStyle}>{@html props.introHtml}</p>
          <StudyPayerSegmentBarListCard
            title={props.segmentListTitle}
            subtitleHtml={props.segmentListSubtitleHtml}
            rows={props.segmentRows}
            maxCount={props.maxPayCount}
            formatValue={props.formatSegmentValue}
            cashPayLabel={props.cashPayLabel}
          />
        </div>
        <div style="display:flex;flex-direction:column;gap:.72rem;">
          {#if d}
            <StudySlideDoughnutCard
              cardClass={d.cardClass ?? ''}
              cardStyle={d.cardStyle}
              active={props.active ?? false}
              title={d.title}
              subtitle={d.subtitle}
              labels={d.labels}
              data={d.data}
              colors={d.colors}
              type={d.type}
              cutout={d.cutout}
              legendPosition={d.legendPosition}
              tooltipFormat={d.tooltipFormat}
              height={d.height ?? '190px'}
            />
          {/if}
          {#if props.cashInsightCard}
            <StudySlideTextCard
              title={props.cashInsightCard.title}
              text={props.cashInsightCard.text}
              icon={props.cashInsightCard.icon}
              cardClass={props.cashInsightCard.cardClass ?? ''}
              cardStyle={props.cashInsightCard.cardStyle}
              titleClass={props.cashInsightCard.titleClass ?? ''}
              titleStyle={props.cashInsightCard.titleStyle}
              footerValue={props.cashInsightCard.footerValue}
              footerCaption={props.cashInsightCard.footerCaption}
            />
          {/if}
          {#if props.footnoteHtml}
            <StudyBlueCalloutCard
              bodyHtml={props.footnoteHtml}
              cardStyle="margin-top:.25rem;"
              paragraphStyle={props.footnoteParagraphStyle ??
                'margin:0;font-size:.84rem;line-height:1.5;'}
            />
          {/if}
        </div>
      </div>
    {:else}
      <div class="hl" style={headlineStyle}>{@html props.headline}</div>
      <p class="bd" style={introStyle}>{@html props.introHtml}</p>
      <StudyPayerSegmentBarListCard
        title={props.segmentListTitle}
        subtitleHtml={props.segmentListSubtitleHtml}
        rows={props.segmentRows}
        maxCount={props.maxPayCount}
        formatValue={props.formatSegmentValue}
        cashPayLabel={props.cashPayLabel}
      />
      {#if props.footnoteHtml}
        <p
          style={props.footnoteParagraphStyle ??
            'margin:.9rem 0 0;font-size:.78rem;line-height:1.5;opacity:.7;'}
        >
          {@html props.footnoteHtml}
        </p>
      {/if}
    {/if}
  </StudySlideContent>
</StudySlide>
